<template>
    <v-container fluid>
      <v-card elevation="2">
        <v-card-title class="d-flex align-center tableheader white--text">
          <v-icon left color="white">mdi-truck-wrench</v-icon>
          Commission and Wages
          <v-spacer/>
          <v-text-field
              v-model="search"
              label="Search (employee / company / payment date)"
              prepend-inner-icon="mdi-magnify"
              density="compact"
              variant="solo-filled"
              flat hide-details single-line
              class="me-4"
          />
          <v-btn color="primary" @click="openDialog(null)" v-if="authStore.canEdit">
            <v-icon left>mdi-plus</v-icon> Add Commission Wage
          </v-btn>
          <ExportButtons entity="commission_wages" filename="Commission Wages" :headers="headers"></ExportButtons>
        </v-card-title>
  
        <v-data-table :items="filtered" :headers="headers" class="elevation-1">
          <template v-slot:[`item.employee_id`]="{ item }">
            {{ findEmployeeName(availableEmployees, item.employee_id) }}
          </template>

          <template v-slot:[`item.status`]="{ item }">
            <v-chip :color="item.status === 'paid' ? 'success' : 'error'" label small>
              {{ findStatusName(item.status) }}
            </v-chip>
          </template> 

          <template v-slot:[`item.payment_category`]="{ item }">
            {{ findPaymentCategoryName(item.payment_category) }}
          </template>

          <template v-slot:[`item.payment_method`]="{ item }">
            <!-- <v-chip :color="item.payment_method === 'check' ? 'success' : item.payment_method === 'cash' ? 'info' : 'error'" variant="outlined"> -->
              {{ findPaymentMethodName(item.payment_method) }}
            <!-- </v-chip> -->
          </template>

          <template v-slot:[`item.amount`]="{ value }">
              {{ formatCurrency(value) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-tooltip location="top" text="View Details">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" small class="me-1" @click="openViewDialog(item)" color="info">
                mdi-eye-outline
              </v-icon>
            </template>
            </v-tooltip>

            <v-tooltip location="top" text="Edit">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" small class="me-1" @click="openDialog(item)" color="secondary" v-if="authStore.isAdmin">
                mdi-pencil
              </v-icon>
            </template>
            </v-tooltip>
            
            <v-tooltip location="top" text="Delete">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" small @click="deleteItemPrompt(item)" color="error" v-if="authStore.isAdmin">
                mdi-delete
              </v-icon>
            </template>
            </v-tooltip>

          </template>
          <template v-slot:[`no-data`]>
            <v-alert :value="true" color="info" icon="mdi-information-variant" class="ma-3">
              {{ tableLoading ? 'Fetching data...' : 'No commission wages available or match your search.' }}
            </v-alert>
          </template>
        </v-data-table>
      </v-card>
  
      <!-- dialog -->
      <v-dialog v-model="dialog" max-width="700">
        <v-card>
          <v-card-title>{{ form.id ? 'Edit' : 'Add' }} Commission Wage</v-card-title>
          <v-form ref="commissionWageForm" v-model="isFormValid" @submit.prevent="save">
           
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12" md="6">
                  <v-select v-model="form.employee_id" label="Employee*" variant="outlined" 
                    :items="availableEmployees"
                    item-title="fullName"
                    item-value="id"
                    :rules="[rules.required]"
                    required/>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model="form.payment_date" label="Payment Date*" variant="outlined" 
                    type="date"
                    class="mb-2"
                    :rules="[rules.required]"
                    required/>  
                </v-col>
              </v-row>
            
              <v-divider></v-divider>
              <v-row>
                <v-col cols="12" md="6">
                  <v-select v-model="form.payment_category" label="Payment Category*" variant="outlined" 
                  :items="commissionWageTypes"
                  item-title="name"
                  item-value="id"
                  :rules="[rules.required]"
                  required/> 
                </v-col>
                <v-col cols="12" md="6">
                  <v-select v-model="form.payment_method" label="Payment Method*" variant="outlined" 
                  :items="paymentMethods"
                  item-title="name"
                  item-value="id"
                  :rules="[rules.required]"
                  required/>  
                </v-col>
              </v-row>
              <v-divider></v-divider>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field v-model="form.commission_split_percentage" label="Commission Split Percentage" variant="outlined" 
                   />  
                </v-col>
                <v-col cols="12" md="6">
                  <v-select v-model="form.status" label="Status*" variant="outlined" 
                  :items="statuses"
                  item-title="name"
                  item-value="id"
                  :rules="[rules.required]"
                  required/> 
                </v-col>
              </v-row>
              <v-divider></v-divider>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field v-model="form.payment_start_date" label="Payment Start Date" variant="outlined" 
                  type="date"
                  class="mb-2"
                  :rules="[rules.required]"
                  required/> 
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model="form.payment_end_date" label="Payment End Date" variant="outlined" 
                    type="date"
                    class="mb-2"
                    :rules="[rules.required]"
                    required/>
                </v-col>
              </v-row>
            <v-divider></v-divider>
              
            <v-textarea v-model="form.description" label="Description*" variant="outlined" 
            :rules="[rules.required]"
            required/> 
            <v-divider></v-divider>
            
            <v-text-field
                      v-model.number="form.amount"
                      label="Amount*"
                      type="number"
                      :prefix="getCurrencyPrefix()"
                      variant="outlined"
                      :rules="[rules.required, rules.positiveNumber]"
                      required
                    ></v-text-field>
            </v-container>
          </v-card-text>

          <v-card-actions>
            <v-spacer/>
            <v-btn text @click="dialog=false">Cancel</v-btn>
            <v-btn color="primary" variant="elevated" type="submit" :loading="dialogLoading" :disabled="!isFormValid">Save</v-btn>
            <!-- @click="save" -->
          </v-card-actions>
          </v-form>
        </v-card>
      </v-dialog>

      <v-dialog v-model="viewDialog" max-width="800px" @update:modelValue="val => !val && closeViewDialog()">
      <v-card :loading="viewDialogLoading">
        <v-card-title class="tableheader white--text d-flex align-center">
          <!-- <v-icon left color="white" class="mr-2">mdi-truck-wrench</v-icon> -->
          <span class="text-h5">Commission Wage Details: {{ viewItem?.employee_id }} {{ viewItem?.company_id }} {{ viewItem?.payment_date }}</span>
          <v-spacer></v-spacer>
          <!-- <v-btn icon="mdi-close" variant="text" color="white" @click="closeViewDialog"></v-btn> -->
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text v-if="viewItem" class="pt-4">
          <v-list lines="two" density="compact">
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Employee:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ findEmployeeName(availableEmployees, viewItem.employee_id) || 'Unknown' }}</v-list-item-text>
            </v-list-item>
            <v-divider inset></v-divider>
            <!-- <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Company:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ findName(companies, viewItem.company_id) || 'Unknown' }}</v-list-item-text>
            </v-list-item> -->
            <v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Payment Date:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ formatDateForDisplay(viewItem.payment_date) }}</v-list-item-text>
            </v-list-item>
            <v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Payment Category:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ findPaymentCategoryName(viewItem.payment_category) }}</v-list-item-text>
            </v-list-item>
            <v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Payment Method:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ findPaymentMethodName(viewItem.payment_method) }}</v-list-item-text>
            </v-list-item>
            <v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Commission Split Percentage:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ viewItem.commission_split_percentage }}</v-list-item-text>
            </v-list-item>
            <v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Payment Start Date:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ formatDateForDisplay(viewItem.payment_start_date) }}</v-list-item-text>
            </v-list-item>
            <v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Payment End Date:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ formatDateForDisplay(viewItem.payment_end_date) }}</v-list-item-text>
            </v-list-item>
            <v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Status:</div>
              </template>
              <v-list-item-text class="text-right pl-2">{{ findStatusName(viewItem.status) }}</v-list-item-text>
            </v-list-item>
          </v-list>
          <v-divider></v-divider>
          <v-list-item>
            <template v-slot:prepend>
              <div class="font-weight-medium">Description:</div>
            </template>
            <v-list-item-text class="text-right pl-2">{{ viewItem.description }}</v-list-item-text>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item>
            <template v-slot:prepend>
              <div class="font-weight-medium">Amount:</div>
            </template>
            <v-list-item-text class="text-right pl-2">{{ formatCurrency(viewItem.amount) }}</v-list-item-text>
          </v-list-item>
        </v-card-text>
        <v-card-text v-else class="text-center py-5">
            <v-progress-circular indeterminate color="primary" class="mb-3"></v-progress-circular>
            <p>Loading commission wage details...</p>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="bg-grey-lighten-4 pa-3">
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="tonal" @click="closeViewDialog">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

      <v-dialog v-model="deleteDialog" max-width="500px">
        <v-card :loading="dialogLoading">
          <v-card-title class="text-h5">Confirm Deletion</v-card-title>
          <v-card-text>Are you sure you want to delete this commission wage: {{ itemToDelete?.employee_id }} {{ itemToDelete?.company_id }} {{ itemToDelete?.payment_date }}?</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue-darken-1" text @click="closeDeleteDialog" :disabled="dialogLoading">Cancel</v-btn>
            <v-btn color="red-darken-1" text @click="confirmDelete" :loading="dialogLoading">Delete</v-btn>
            <v-spacer></v-spacer>
          </v-card-actions>
        </v-card>
      </v-dialog>
  
      <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout" bottom right>
        {{ snackbar.message }}
        <template v-slot:actions>
          <v-btn color="white" variant="text" @click="snackbar.show = false">Close</v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </template>
  
  <script setup>
  import { ref, computed, onMounted, nextTick } from 'vue';
  import CommissionsWagesService from '@/services/CommissionsWagesService';
  import { useAuthStore } from '@/stores/auth'; 
  import { useSnackbar, formatCurrency, formatDateForDisplay, getCurrencyPrefix } from '@/services/genFunctions';
  import ExportButtons from '@/components/ExportButtons.vue';
  import EmployeesService from '@/services/EmployeesService';

  const { snackbar, showSnackbar } = useSnackbar();
  const authStore = useAuthStore();
  const commissionWages  = ref([]);
  const search   = ref('');
  const dialog   = ref(false);
  const form     = ref({ id:null, employee_id:'', company_id:'', payment_date:'', payment_category:'', payment_method:'', commission_split_percentage:'', status:'', description:'', payment_start_date:'', payment_end_date:''});
  const deleteDialog = ref(false);
  const itemToDelete = ref(null);
  const dialogLoading = ref(false);
  const viewDialog = ref(false);
  const viewItem = ref(null);
  const viewDialogLoading = ref(false);
  const tableLoading = ref(true);
  const commissionWageForm = ref(null);
  const isFormValid = ref(false);
  const employees = ref([]);
  const loadEmployees = async () => employees.value = await EmployeesService.getEmployees();
  const availableEmployees = ref([]);
  const rules = {
    required: value => !!value || 'This field is required.',
  };

  const headers = [
    { title:'Employee', key:'employee_id' },
    // { title:'Company', key:'company_id' },
    { title:'Payment Date', key:'payment_date' },
    { title:'Payment Category', key:'payment_category' },
    { title:'Description', key:'description' },
    { title:'Payment Method', key:'payment_method' },
    { title:'Commission Split %', key:'commission_split_percentage', width: '200px' },
    { title:'Payment Start Date', key:'payment_start_date', width: '200px' },
    { title:'Payment End Date', key:'payment_end_date', width: '200px' },
    { title:'Status', key:'status' },
    { title:'Amount', key:'amount' },
    { title:'Actions', key:'actions', sortable:false, width: '160px' }
  ];
  
  const load = async () => commissionWages.value = await CommissionsWagesService.getCommissionWages();
  
  onMounted(async () => {
    tableLoading.value = true;
    try {
      await load();
      await loadEmployees();

      availableEmployees.value = employees.value.map(t => ({ ...t, fullName: `${t.first_name} ${t.last_name}` }));
      // console.log(availableEmployees.value);
    } catch (error) {
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          showSnackbar(error.response.data.error || 'You are not authorized to view commission wages.', 'error');
      } else {
          showSnackbar('Failed to load commission wages.', 'error');
      }
      console.error("Error fetching commission wages:", error);
      commissionWages.value = [];
    } finally {
      tableLoading.value = false;
    }
  });
  
  const filtered = computed(() =>
    commissionWages.value.filter(c =>
      (c.employee_id+c.company_id+c.payment_date).toLowerCase().includes(search.value.toLowerCase())
    )
  );

  const paymentMethods = [
    { id: 'check', name: 'Check' },
    { id: 'cash', name: 'Cash' },
    { id: 'banktransfer', name: 'Bank Transfer' },
    { id: 'creditcard', name: 'Credit Card' },
    { id: 'mobilemoney', name: 'Mobile Money' }
  ];

  const statuses = [
    { id: 'paid', name: 'Paid' },
    { id: 'pending', name: 'Pending' },
    { id: 'voided', name: 'Voided' }
  ];

  const commissionWageTypes = [
    { id: 'salary', name: 'Salary' },
    { id: 'commission', name: 'Commission' }
  ];

  function openDialog(item) {
    form.value = item ? { ...item } : { id:null, employee_id:'', company_id:'', payment_date:'', payment_method:'', commission_split_percentage:'', status:'', description:'' };
    dialog.value = true;
  }

  async function save() {
    if (!commissionWageForm.value) return;
    const { valid } = await commissionWageForm.value.validate();
    if (!valid) {
      showSnackbar('Please correct the form errors.', 'warning');
      return;
    }

    dialogLoading.value = true;
    const { id, ...data } = form.value;
    id ? await CommissionsWagesService.updateCommissionWage(id, data)
       : await CommissionsWagesService.createCommissionWage(data);
    dialog.value = false;
    await load();
    dialogLoading.value = false;
    showSnackbar('Commission wage saved successfully!', 'success');
  }

  const deleteItemPrompt = (item) => {
    itemToDelete.value = { ...item };
    deleteDialog.value = true;
  };

  const closeDeleteDialog = () => {
    deleteDialog.value = false;
    nextTick(() => itemToDelete.value = null);
    dialogLoading.value = false;
  };

  const confirmDelete = async () => {
    if (!itemToDelete.value?.id) return;
    dialogLoading.value = true;
    try {
        await CommissionsWagesService.deleteCommissionWage(itemToDelete.value.id);
        commissionWages.value = commissionWages.value.filter(c => c.id !== itemToDelete.value.id);
        showSnackbar('Commission wage deleted successfully!', 'success');
        closeDeleteDialog();
    } catch (error) {
        const errorMsg = error.response?.data?.error || 'Could not delete commission wage.';
        showSnackbar(errorMsg, 'error', 6000);
        console.error("Error deleting commission wage:", error.response || error);
    } finally {
        dialogLoading.value = false;
    }
  };

  const openViewDialog = async (item) => {
    viewDialogLoading.value = true;
    viewDialog.value = true;
    viewItem.value = null; 
    try {
      const fullCommissionWageData = await CommissionsWagesService.getCommissionWage(item.id);
      viewItem.value = fullCommissionWageData;
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to load commission wage details.';
      showSnackbar(errorMsg, 'error');
      console.error("Error fetching commission wage details for view:", error);
      viewDialog.value = false; 
    } finally {
      viewDialogLoading.value = false;
    }
  };

  const closeViewDialog = () => {
    viewDialog.value = false;
    viewItem.value = null;
  };

  const findPaymentMethodName = (paymentMethod) => {
    const paymentMethodObj = paymentMethods.find(p => p.id === paymentMethod);
    return paymentMethodObj ? paymentMethodObj.name : 'Unknown';
  };

  const findStatusName = (status) => {
    const statusObj = statuses.find(s => s.id === status);
    return statusObj ? statusObj.name : 'Unknown';
  };

  const findPaymentCategoryName = (paymentCategory) => {
    const paymentCategoryObj = commissionWageTypes.find(p => p.id === paymentCategory);
    return paymentCategoryObj ? paymentCategoryObj.name : 'Unknown';
  };
  
  const findEmployeeName = (list, id) => {
    const obj = list.find(i => i && i.id === id);
    return obj ? obj.first_name + ' ' + obj.last_name : `Unknown (ID: ${id})`;
  };
  </script>
  